import {
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { Contact, ContactExtended } from '../../../../../../types/api';
import { useAuth } from '../../../hooks';
import { getAccountContactsAPI } from '../../../services/apis';
import SearchInput from '../../inputs/SearchInput';
import AccountContactsFilterMenu from './AccountContactsFilterMenu';
import AccountContactsMobileList from './AccountContactsMobileList';
import AccountContactsTable from './AccountContactsTable';
import ActivityLogModal from './ActivityLogModal';
import ContactFormModal from './ContactFormModal';

export default function TeamSettings() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [query, setQuery] = useState('');
  const [showInactiveUsers, setShowInactiveUsers] = useState(true);
  const [activityLogModalOpen, setActivityLogModalOpen] = useState(false);
  const [modalState, setModalState] = useState<{
    open: boolean;
    contact?: Contact;
  }>({ open: false, contact: undefined });
  const { data: contacts, isLoading } = useQuery<ContactExtended[]>(
    ['contacts', user?.account.id],
    async () => (user?.account.id ? getAccountContactsAPI() : []),
    { enabled: !!user?.account.id, refetchOnWindowFocus: false }
  );
  const filteredContacts = (contacts ?? [])
    .map((c) => ({ ...c, status: c.status ? c.status : 'Inactive' }))
    .filter((contact) => {
      const match = query
        ? contact.name.toLowerCase().includes(query.toLowerCase()) ||
          contact.email?.toLowerCase().includes(query.toLowerCase())
        : true;
      return showInactiveUsers ? match : match && contact.status !== 'Inactive';
    })
    .sort((a, b) => {
      // Status order: Active, Unverified, Invited, Inactive
      const statusOrder = ['Active', 'Unverified', 'Invited', 'Inactive'];
      const installerRoleOrder = ['Admin', 'Accounting', 'Sales'];

      const aStatusIdx = statusOrder.indexOf(a.status ?? '');
      const bStatusIdx = statusOrder.indexOf(b.status ?? '');
      if (aStatusIdx === -1 && b.status !== 'Inactive') return 1;
      if (bStatusIdx === -1 && a.status !== 'Inactive') return -1;
      if (aStatusIdx !== bStatusIdx) {
        return aStatusIdx - bStatusIdx;
      }

      const aRoleIdx = installerRoleOrder.indexOf(a.installer_role ?? '');
      const bRoleIdx = installerRoleOrder.indexOf(b.installer_role ?? '');
      if (aRoleIdx !== bRoleIdx) {
        return aRoleIdx - bRoleIdx;
      }

      // name should be available and fallback to empty string if not
      return a.name.localeCompare(b.name);
    });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  return (
    <>
      <Box flex={1} px={2} py={2}>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Manage user accounts and permissions for users added for{' '}
          {user?.account?.business_name ?? 'your business'}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          mb={1.5}
          fontStyle="italic"
        >
          This tab is only accessible to users with Admin role
        </Typography>

        <Box
          bgcolor="#F6F8FB"
          borderColor="#DCE1ED"
          border={0.25}
          p={1.25}
          borderRadius={0.5}
          mb={2.25}
        >
          <Typography variant="body2" color="text.secondary">
            <b>Note:</b> Email addresses cannot be edited after creation. If an
            email was entered incorrectly, you must archive the user and create
            a new account with the correct email address.
          </Typography>
        </Box>

        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          gap={2.25}
        >
          <Grid xs={12} sm={4}>
            <Box display="flex" gap={1} alignItems="center">
              <SearchInput
                placeholder="Search users"
                value={query}
                onChange={handleSearchChange}
                fullWidth
              />

              <AccountContactsFilterMenu
                showInactiveUsers={showInactiveUsers}
                setShowInactiveUsers={setShowInactiveUsers}
                setActivityLogModalOpen={setActivityLogModalOpen}
              />
            </Box>
          </Grid>

          <Grid xs={12} sm={3}>
            <Button
              color="primary"
              variant="contained"
              fullWidth
              onClick={() => setModalState({ open: true, contact: undefined })}
            >
              Add New User
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isMobile ? (
        <AccountContactsMobileList
          data={filteredContacts}
          isLoading={isLoading}
          setModalState={setModalState}
        />
      ) : (
        <AccountContactsTable
          data={filteredContacts}
          isLoading={isLoading}
          setModalState={setModalState}
        />
      )}

      {modalState.open && (
        <ContactFormModal
          contact={modalState.contact}
          open={modalState.open}
          onClose={() => setModalState({ open: false, contact: undefined })}
        />
      )}

      <ActivityLogModal
        open={activityLogModalOpen}
        onClose={() => setActivityLogModalOpen(false)}
      />
    </>
  );
}
